import { useCallback } from 'react';

import { useLocalStorage } from '@/hooks/local-storage';

const STORAGE_KEY = 'parent_expanded_children';

/** Stable so `useLocalStorage` isn't handed a fresh object every render. */
const NO_STORED_STATE: Record<string, boolean> = {};

export interface ChildSectionCollapse {
  isExpanded: (childId: number) => boolean;
  setExpanded: (childId: number, expanded: boolean) => void;
}

/**
 * Whether each child's section is expanded, remembered across the parent views.
 *
 * A parent with several children lands on a page taller than the screen, so
 * only the first child starts open and the siblings collapse to their header —
 * enough to see who is there and how many rows they have, without scrolling
 * past one child to reach the next.
 *
 * Toggles are stored in localStorage rather than the URL: how a parent chooses
 * to fold their own view is not part of what a shared link should carry, and it
 * outlives navigating between the list pages. Only explicit toggles are written,
 * so the default keeps applying to children that were never touched.
 */
export function useChildSectionCollapse(defaultExpandedChildId: number | 'all' | undefined): ChildSectionCollapse {
  const [expanded, setExpanded] = useLocalStorage<Record<string, boolean>>(STORAGE_KEY, NO_STORED_STATE);

  const isExpanded = useCallback((childId: number) => expanded[childId] ?? (defaultExpandedChildId === 'all' || childId === defaultExpandedChildId), [expanded, defaultExpandedChildId]);

  const setChildExpanded = useCallback((childId: number, value: boolean) => setExpanded((previous) => ({ ...previous, [childId]: value })), [setExpanded]);

  return { isExpanded, setExpanded: setChildExpanded };
}
